import React, { useState, useEffect } from 'react';
import { useParams, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  CircularProgress,
  Button,
  Link,
  Stack,
  Alert
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import EditIcon from '@mui/icons-material/Edit';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import InfoIcon from '@mui/icons-material/Info';
import { apiClient } from '../api/apiClient';
import { useAuth } from '../auth/AuthContext';

const INSTANZ_CODE_TO_LEVEL = {
  SG: 1,
  LSG: 2,
  BSG: 3
};

const deriveInstanzLevel = (entscheidung) => {
  if (!entscheidung) {
    return null;
  }

  const instanzCode = entscheidung.gericht?.instanz;
  if (instanzCode && INSTANZ_CODE_TO_LEVEL[instanzCode]) {
    return INSTANZ_CODE_TO_LEVEL[instanzCode];
  }

  if (entscheidung.gericht && entscheidung.gericht_3_instanz?.id === entscheidung.gericht.id) {
    return 3;
  }
  if (entscheidung.gericht && entscheidung.gericht_2_instanz?.id === entscheidung.gericht.id) {
    return 2;
  }
  if (entscheidung.gericht && entscheidung.gericht_1_instanz?.id === entscheidung.gericht.id) {
    return 1;
  }

  if (entscheidung.aktenzeichen && entscheidung.aktenzeichen === entscheidung.aktenzeichen_3_instanz) {
    return 3;
  }
  if (entscheidung.aktenzeichen && entscheidung.aktenzeichen === entscheidung.aktenzeichen_2_instanz) {
    return 2;
  }
  if (entscheidung.aktenzeichen && entscheidung.aktenzeichen === entscheidung.aktenzeichen_1_instanz) {
    return 1;
  }

  return null;
};

const selectAktenzeichenForTitle = (entscheidung) => {
  if (!entscheidung) {
    return null;
  }

  const instanzLevel = deriveInstanzLevel(entscheidung);

  if (instanzLevel === 3 && (entscheidung.aktenzeichen_3_instanz || entscheidung.aktenzeichen)) {
    return entscheidung.aktenzeichen_3_instanz || entscheidung.aktenzeichen;
  }
  if (instanzLevel === 2 && (entscheidung.aktenzeichen_2_instanz || entscheidung.aktenzeichen)) {
    return entscheidung.aktenzeichen_2_instanz || entscheidung.aktenzeichen;
  }
  if (instanzLevel === 1 && (entscheidung.aktenzeichen_1_instanz || entscheidung.aktenzeichen)) {
    return entscheidung.aktenzeichen_1_instanz || entscheidung.aktenzeichen;
  }

  return (
    entscheidung.aktenzeichen ||
    entscheidung.aktenzeichen_1_instanz ||
    entscheidung.aktenzeichen_2_instanz ||
    entscheidung.aktenzeichen_3_instanz ||
    null
  );
};

const EntscheidungDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const { token } = useAuth();
  const [entscheidung, setEntscheidung] = useState(location.state?.entscheidung || null);
  const [loading, setLoading] = useState(!location.state?.entscheidung);
  const [error, setError] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [sachgebiete, setSachgebiete] = useState(
    location.state?.entscheidung?.available_sachgebiete?.reduce((acc, sg) => {
      acc[sg.code] = sg.name;
      return acc;
    }, {}) || {}
  );

  useEffect(() => {
    // Nur laden wenn keine Entscheidung im State ist
    if (!location.state?.entscheidung) {
      const fetchEntscheidung = async () => {
        try {
          setLoading(true);
          setError(null);
          const response = await apiClient.get(`/public/entscheidungen/${id}`, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
          });
          // Sachgebiete in ein Objekt umwandeln für einfacheren Zugriff
          const sachgebieteMap = {};
          response.data.available_sachgebiete.forEach(sg => {
            sachgebieteMap[sg.code] = sg.name;
          });
          setSachgebiete(sachgebieteMap);
          setEntscheidung(response.data);
          setError(null);
        } catch (error) {
          console.error('Fehler beim Laden der Entscheidung:', error);
          
          // Detaillierte Fehlerbehandlung
          let errorMessage = 'Die Entscheidung konnte nicht geladen werden.';
          
          if (error.response) {
            const { status, data } = error.response;
            switch (status) {
              case 404:
                errorMessage = 'Entscheidung nicht gefunden.';
                break;
              case 403:
                errorMessage = 'Keine Berechtigung zum Anzeigen dieser Entscheidung.';
                break;
              case 401:
                errorMessage = 'Nicht autorisiert - bitte melden Sie sich an.';
                break;
              case 500:
                errorMessage = 'Serverfehler - bitte versuchen Sie es später erneut.';
                break;
              default:
                errorMessage = data?.detail || `Fehler ${status}: Die Entscheidung konnte nicht geladen werden.`;
            }
          } else if (error.request) {
            errorMessage = 'Keine Verbindung zum Server - bitte prüfen Sie Ihre Internetverbindung.';
          } else {
            errorMessage = error.message || 'Ein unbekannter Fehler ist aufgetreten.';
          }
          
          setError(errorMessage);
        } finally {
          setLoading(false);
        }
      };

      fetchEntscheidung();
    }
  }, [id, token, location.state?.entscheidung]);

  // Aktualisiere den Titel der Webseite
  useEffect(() => {
    const titleSuffix = ' - Sozialgerichtsbarkeit';
    const aktenzeichenForTitle = selectAktenzeichenForTitle(entscheidung);

    if (aktenzeichenForTitle) {
      document.title = `${aktenzeichenForTitle}${titleSuffix}`;
    } else {
      document.title = `Entscheidung${titleSuffix}`;
    }

    // Cleanup: Setze den Titel zurück wenn die Komponente unmounted wird
    return () => {
      document.title = 'Sozialgerichtsbarkeit';
    };
  }, [entscheidung]);

  const handlePdfDownload = async () => {
    try {
      setDownloadingPdf(true);
      setPdfError(null);
      
      const response = await apiClient.get(`/public/entscheidungen/${id}/pdf`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      // Base64-String in Blob umwandeln
      const byteCharacters = atob(response.data.pdf_base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      
      // Download initiieren
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', response.data.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Fehler beim PDF-Download:', error);
      
      // Detaillierte Fehlerbehandlung für PDF-Download
      let errorMessage = 'Das PDF konnte nicht heruntergeladen werden.';
      
      if (error.response) {
        const { status, data } = error.response;
        switch (status) {
          case 404:
            errorMessage = 'PDF nicht verfügbar für diese Entscheidung.';
            break;
          case 403:
            errorMessage = 'Keine Berechtigung zum Download dieser Entscheidung.';
            break;
          case 401:
            errorMessage = 'Nicht autorisiert - bitte melden Sie sich an.';
            break;
          case 500:
            errorMessage = 'Serverfehler beim Generieren des PDFs - bitte versuchen Sie es später erneut.';
            break;
          default:
            errorMessage = data?.detail || `Fehler ${status}: PDF-Download fehlgeschlagen.`;
        }
      } else if (error.request) {
        errorMessage = 'Keine Verbindung zum Server - bitte prüfen Sie Ihre Internetverbindung.';
      } else {
        errorMessage = error.message || 'Ein unbekannter Fehler beim PDF-Download ist aufgetreten.';
      }
      
      setPdfError(errorMessage);
    } finally {
      setDownloadingPdf(false);
    }
  };

  const baseBoxProps = {
    sx: {
      width: '100%',
      maxWidth: '1200px',
      mx: 'auto',
      px: { xs: 1.5, sm: 2, md: 0 },
      py: { xs: 2, md: 3 }
    }
  };

  if (loading) {
    return (
      <Box {...baseBoxProps} sx={{ ...baseBoxProps.sx, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box {...baseBoxProps}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Fehler beim Laden der Entscheidung
          </Typography>
          <Typography variant="body1">
            {error}
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Button 
              variant="outlined" 
              onClick={() => window.location.reload()}
            >
              Seite neu laden
            </Button>
          </Box>
        </Alert>
      </Box>
    );
  }

  if (!entscheidung) {
    return (
      <Box {...baseBoxProps}>
        <Typography>Entscheidung nicht gefunden</Typography>
      </Box>
    );
  }

  return (
    <Box {...baseBoxProps}>
      {/* PDF-Fehleranzeige */}
      {pdfError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPdfError(null)}>
          {pdfError}
        </Alert>
      )}

      {/* Basisinfos */}
      <Paper sx={{ p: { xs: 2, md: 3 }, mb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon color="primary" />
            Basisinfos
          </Typography>
          <Stack direction="row" spacing={1}>
            {token && (
              <Button
                variant="outlined"
                color="primary"
                startIcon={<EditIcon />}
                component={RouterLink}
                to={`/admin/entscheidungen/edit/${id}`}
              >
                Bearbeiten
              </Button>
            )}
            <Button
              variant="outlined"
              color="primary"
              startIcon={downloadingPdf ? <CircularProgress size={20} /> : <PictureAsPdfIcon />}
              onClick={handlePdfDownload}
              disabled={downloadingPdf}
            >
              {downloadingPdf ? 'Wird heruntergeladen...' : 'PDF herunterladen'}
            </Button>
          </Stack>
        </Box>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} md={3}>
            <Typography variant="subtitle2" color="textSecondary">
              Aktenzeichen
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 'medium', mt: 0.5 }}>
              {entscheidung.aktenzeichen}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" color="textSecondary">
              Gericht
            </Typography>
            <Link
              component={RouterLink}
              to={`/gerichte/${entscheidung.gericht?.id}`}
              sx={{ 
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                color: 'primary.main',
                mt: 0.5,
                '&:hover': { textDecoration: 'none', color: 'primary.dark' }
              }}
            >
              <AccountBalanceIcon />
              <Typography variant="h6">
                {entscheidung.gericht?.bezeichnung || '-'}
              </Typography>
            </Link>
          </Grid>
          <Grid item xs={12} md={2.5}>
            <Typography variant="subtitle2" color="textSecondary">
              Kategorie
            </Typography>
            <Chip 
              label={entscheidung.kategorie === 'U' ? 'Urteil' : 
                     entscheidung.kategorie === 'B' ? 'Beschluss' : 
                     entscheidung.kategorie === 'G' ? 'Gerichtsbescheid' : 
                     entscheidung.kategorie}
              color="primary"
              sx={{ 
                mt: 0.5,
                height: 32,
                '& .MuiChip-label': {
                  fontSize: '1.15rem'
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={2.5}>
            <Typography variant="subtitle2" color="textSecondary">
              Sachgebiet
            </Typography>
            <Chip 
              label={sachgebiete[entscheidung.sachgebiet] || '-'}
              color="primary"
              sx={{ 
                mt: 0.5,
                height: 32,
                '& .MuiChip-label': {
                  fontSize: '1.15rem'
                }
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Relationen Kachel */}
      <Paper sx={{ p: { xs: 2, md: 3 }, mb: 1 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccountBalanceIcon color="primary" />
          Beziehungen
        </Typography>
        <Grid container spacing={2}>
          {/* 1. Instanz */}
          <Grid item xs={12} md={4}>
            <Box sx={{ 
              p: 2, 
              border: '1px solid',
              borderWidth: entscheidung.aktenzeichen === entscheidung.aktenzeichen_1_instanz || 
                         (!entscheidung.aktenzeichen_1_instanz && !entscheidung.aktenzeichen_2_instanz && !entscheidung.aktenzeichen_3_instanz) ? 
                         4 : 1,
              borderColor: entscheidung.aktenzeichen === entscheidung.aktenzeichen_1_instanz || 
                         (!entscheidung.aktenzeichen_1_instanz && !entscheidung.aktenzeichen_2_instanz && !entscheidung.aktenzeichen_3_instanz) ? 
                         'primary.main' : 'divider',
              borderRadius: 1,
              height: '100%',
              boxShadow: entscheidung.aktenzeichen === entscheidung.aktenzeichen_1_instanz || 
                        (!entscheidung.aktenzeichen_1_instanz && !entscheidung.aktenzeichen_2_instanz && !entscheidung.aktenzeichen_3_instanz) ? 
                        3 : 0,
              bgcolor: entscheidung.aktenzeichen === entscheidung.aktenzeichen_1_instanz || 
                      (!entscheidung.aktenzeichen_1_instanz && !entscheidung.aktenzeichen_2_instanz && !entscheidung.aktenzeichen_3_instanz) ? 
                      'primary.lighter' : 'transparent'
            }}>
              <Typography variant="subtitle1" color="primary" gutterBottom>
                1. Instanz
              </Typography>
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Gericht
                </Typography>
                {entscheidung.gericht_1_instanz ? (
                  <Link
                    component={RouterLink}
                    to={`/gerichte/${entscheidung.gericht_1_instanz.id}`}
                    sx={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      color: 'primary.main',
                      '&:hover': { textDecoration: 'none', color: 'primary.dark' }
                    }}
                  >
                    <AccountBalanceIcon fontSize="small" />
                    {entscheidung.gericht_1_instanz.bezeichnung}
                  </Link>
                ) : '-'}
              </Box>
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Aktenzeichen
                </Typography>
                <Typography variant="body2">
                  {entscheidung.aktenzeichen_1_instanz || entscheidung.aktenzeichen || '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">
                  Datum
                </Typography>
                <Typography variant="body2">
                  {entscheidung.datum_1_instanz ? 
                    new Date(entscheidung.datum_1_instanz).toLocaleDateString('de-DE') : '-'}
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* 2. Instanz */}
          <Grid item xs={12} md={4}>
            <Box sx={{ 
              p: 2, 
              border: '1px solid',
              borderWidth: entscheidung.aktenzeichen === entscheidung.aktenzeichen_2_instanz ? 4 : 1,
              borderColor: entscheidung.aktenzeichen === entscheidung.aktenzeichen_2_instanz ? 'primary.main' : 'divider',
              borderRadius: 1,
              height: '100%',
              boxShadow: entscheidung.aktenzeichen === entscheidung.aktenzeichen_2_instanz ? 3 : 0,
              bgcolor: entscheidung.aktenzeichen === entscheidung.aktenzeichen_2_instanz ? 'primary.lighter' : 'transparent',
              opacity: entscheidung.gericht_2_instanz || entscheidung.aktenzeichen_2_instanz ? 1 : 0.5
            }}>
              <Typography variant="subtitle1" color="primary" gutterBottom>
                2. Instanz
              </Typography>
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Gericht
                </Typography>
                {entscheidung.gericht_2_instanz ? (
                  <Link
                    component={RouterLink}
                    to={`/gerichte/${entscheidung.gericht_2_instanz.id}`}
                    sx={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      color: 'primary.main',
                      '&:hover': { textDecoration: 'none', color: 'primary.dark' }
                    }}
                  >
                    <AccountBalanceIcon fontSize="small" />
                    {entscheidung.gericht_2_instanz.bezeichnung}
                  </Link>
                ) : '-'}
              </Box>
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Aktenzeichen
                </Typography>
                <Typography variant="body2">
                  {entscheidung.aktenzeichen_2_instanz || '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">
                  Datum
                </Typography>
                <Typography variant="body2">
                  {entscheidung.datum_2_instanz ? 
                    new Date(entscheidung.datum_2_instanz).toLocaleDateString('de-DE') : '-'}
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* 3. Instanz */}
          <Grid item xs={12} md={4}>
            <Box sx={{ 
              p: 2, 
              border: '1px solid',
              borderWidth: entscheidung.aktenzeichen === entscheidung.aktenzeichen_3_instanz ? 4 : 1,
              borderColor: entscheidung.aktenzeichen === entscheidung.aktenzeichen_3_instanz ? 'primary.main' : 'divider',
              borderRadius: 1,
              height: '100%',
              boxShadow: entscheidung.aktenzeichen === entscheidung.aktenzeichen_3_instanz ? 3 : 0,
              bgcolor: entscheidung.aktenzeichen === entscheidung.aktenzeichen_3_instanz ? 'primary.lighter' : 'transparent',
              opacity: entscheidung.gericht_3_instanz || entscheidung.aktenzeichen_3_instanz ? 1 : 0.5
            }}>
              <Typography variant="subtitle1" color="primary" gutterBottom>
                3. Instanz
              </Typography>
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Gericht
                </Typography>
                {entscheidung.gericht_3_instanz ? (
                  <Link
                    component={RouterLink}
                    to={`/gerichte/${entscheidung.gericht_3_instanz.id}`}
                    sx={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      color: 'primary.main',
                      '&:hover': { textDecoration: 'none', color: 'primary.dark' }
                    }}
                  >
                    <AccountBalanceIcon fontSize="small" />
                    {entscheidung.gericht_3_instanz.bezeichnung}
                  </Link>
                ) : '-'}
              </Box>
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Aktenzeichen
                </Typography>
                <Typography variant="body2">
                  {entscheidung.aktenzeichen_3_instanz || '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">
                  Datum
                </Typography>
                <Typography variant="body2">
                  {entscheidung.datum_3_instanz ? 
                    new Date(entscheidung.datum_3_instanz).toLocaleDateString('de-DE') : '-'}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Bemerkung */}
      {entscheidung.bemerkung && (
        <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
          <Typography variant="subtitle2" color="textSecondary">
            Bemerkung
          </Typography>
          <Typography 
            variant="body1" 
            component="div"
            sx={{
              '& p': { marginBottom: 2 },
              '& br': { marginBottom: 1 }
            }}
            dangerouslySetInnerHTML={{ __html: entscheidung.bemerkung }}
          />
        </Paper>
      )}

      {/* Leitsätze */}
      {entscheidung.leitsaetze && entscheidung.leitsaetze.trim() !== '' && (
        <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Leitsätze
          </Typography>
          <Typography 
            variant="body1" 
            component="div"
            sx={{
              '& p': { marginBottom: 2 },
              '& br': { marginBottom: 1 }
            }}
            dangerouslySetInnerHTML={{ __html: entscheidung.leitsaetze }} 
          />
        </Paper>
      )}

      {/* Deskriptoren */}
      {entscheidung.deskriptoren && entscheidung.deskriptoren.trim() !== '' && (
        <Paper sx={{ p: { xs: 1.5, md: 2 }, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Deskriptoren
          </Typography>
          <Typography
            component="div"
            dangerouslySetInnerHTML={{ __html: entscheidung.deskriptoren }}
          />
        </Paper>
      )}

      {/* Normenkette */}
      {entscheidung.normenkette && entscheidung.normenkette.trim() !== '' && (
        <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Normenkette
          </Typography>
          <Typography 
            variant="body1" 
            component="div"
            sx={{
              '& p': { marginBottom: 2 },
              '& br': { marginBottom: 1 }
            }}
            dangerouslySetInnerHTML={{ __html: entscheidung.normenkette }} 
          />
        </Paper>
      )}

      {/* Entscheidungstext */}
      {entscheidung.text && (
        <Paper sx={{ p: { xs: 2, md: 3 } }}>
          <Typography variant="h6" gutterBottom>
            Entscheidungstext
          </Typography>
          <Typography 
            variant="body1" 
            component="div"
            sx={{
              '& p': { marginBottom: 2 },
              '& br': { marginBottom: 1 }
            }}
            dangerouslySetInnerHTML={{ __html: entscheidung.text }}
          />
        </Paper>
      )}
    </Box>
  );
};

export default EntscheidungDetail; 